import { useState, useCallback } from "react"
import type { FC } from "react"
import {
  useLikePost,
  useRefreshPostMessages,
  useNewCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useModerateCommentMutation,
} from "../../hooks/usePosts"
import type { Comment, LikeUser } from "../../models/posts"

interface CommentsProps {
  postId: number
  userLike: boolean
  likes: LikeUser[]
  comments: Comment[]
  commentsLength: number
}

const Comments: FC<CommentsProps> = ({ postId, userLike, likes, comments, commentsLength }) => {
  const [newComment, setNewComment] = useState("")
  const likeMutation = useLikePost()
  const refreshPost = useRefreshPostMessages()
  const newCommentMutation = useNewCommentMutation()

  const handleLike = () => {
    if (likeMutation.isPending) return
    likeMutation.mutate(postId)
  }

  const handleNewComment = useCallback(() => {
    if (!newComment.trim() || newCommentMutation.isPending) return
    setNewComment("")
    newCommentMutation.mutate(
      { postId, parentId: 0, content: newComment },
      { onSuccess: () => refreshPost(postId) },
    )
  }, [newComment, postId, refreshPost, newCommentMutation])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleNewComment()
    }
  }

  return (
    <div className="border-t border-gray-100 px-4 py-3">
      <div className="flex items-center justify-between mb-3">
        <button
          className={`flex items-center gap-1 text-sm ${userLike ? "text-red-500" : "text-gray-500 hover:text-red-500"} ${likeMutation.isPending ? "opacity-50" : ""}`}
          onClick={handleLike}
          disabled={likeMutation.isPending}
        >
          <i className="fa fa-heart" /> Like
          {likes.length > 0 && <span className="text-xs text-gray-400 ml-1">{likes.length}</span>}
        </button>
        <span className="text-xs text-gray-400">
          {commentsLength} Comentário{commentsLength !== 1 ? "s" : ""}
        </span>
      </div>

      {comments.length > 0 && (
        <div className="space-y-2 mb-3">
          {comments.map((comment) => (
            <CommentItem key={comment.id} comment={comment} postId={postId} onRefresh={() => refreshPost(postId)} />
          ))}
        </div>
      )}

      <div className="flex items-start gap-2">
        <input
          type="text"
          className="flex-1 border border-gray-200 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:border-blue-300"
          placeholder="Escreva um comentário..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          className="text-blue-500 hover:text-blue-700 px-2 py-1 text-sm disabled:opacity-50"
          disabled={!newComment.trim() || newCommentMutation.isPending}
          onClick={handleNewComment}
        >
          {newCommentMutation.isPending ? (
            <i className="fa fa-spinner fa-spin" />
          ) : (
            <i className="fa fa-send" />
          )}
        </button>
      </div>
    </div>
  )
}

interface CommentItemProps {
  comment: Comment
  postId: number
  onRefresh: () => void
}

const CommentItem: FC<CommentItemProps> = ({ comment, postId, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isReplying, setIsReplying] = useState(false)
  const [editContent, setEditContent] = useState(comment.content)
  const [replyContent, setReplyContent] = useState("")
  const [avatarError, setAvatarError] = useState(false)
  const updateMutation = useUpdateCommentMutation()
  const replyMutation = useNewCommentMutation()
  const deleteMutation = useDeleteCommentMutation()
  const moderateMutation = useModerateCommentMutation()

  const handleEdit = () => {
    if (!editContent.trim() || updateMutation.isPending) return
    updateMutation.mutate(
      { commentId: comment.id, content: editContent },
      { onSuccess: () => { setIsEditing(false); onRefresh() } },
    )
  }

  const handleReply = () => {
    if (!replyContent.trim() || replyMutation.isPending) return
    replyMutation.mutate(
      { postId, parentId: comment.report_to, content: replyContent },
      { onSuccess: () => { setIsReplying(false); setReplyContent(""); onRefresh() } },
    )
  }

  const handleDelete = () => {
    if (window.confirm("Tem a certeza que pretende eliminar o comentário?")) {
      deleteMutation.mutate(comment.id, { onSuccess: () => onRefresh() })
    }
  }

  const handleModerate = () => {
    moderateMutation.mutate(comment.id, { onSuccess: () => onRefresh() })
  }

  return (
    <div className="flex gap-2">
      {avatarError || !comment.user.avatar ? (
        <div className="w-6 h-6 rounded-full flex items-center justify-center bg-blue-100 text-blue-600 text-[10px] font-bold mt-0.5 shrink-0">
          {comment.user.name?.charAt(0)?.toUpperCase() || "?"}
        </div>
      ) : (
        <img src={comment.user.avatar} alt="" className="w-6 h-6 rounded-full mt-0.5" onError={() => setAvatarError(true)} />
      )}
      <div className="flex-1 min-w-0">
        <div className={`bg-gray-50 rounded-lg p-2 ${comment.status === 5 ? "border border-yellow-300" : ""}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-gray-900">{comment.user.name}</span>
            <span className="text-xs text-gray-400">{comment.date}</span>
          </div>
          {isEditing ? (
            <div className="mt-1">
              <input
                type="text"
                className="w-full border border-gray-200 rounded px-2 py-1 text-sm"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleEdit() } }}
              />
              <div className="flex gap-2 mt-1">
                <button
                  className="text-xs text-blue-500 disabled:opacity-50"
                  onClick={handleEdit}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? <i className="fa fa-spinner fa-spin" /> : "Guardar"}
                </button>
                <button
                  className="text-xs text-gray-400 disabled:opacity-50"
                  onClick={() => setIsEditing(false)}
                  disabled={updateMutation.isPending}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-700 mt-0.5">{comment.content}</p>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
          {comment.can_edit && (
            <button className="hover:text-gray-600" onClick={() => setIsEditing(true)}>Editar</button>
          )}
          <button className="hover:text-gray-600" onClick={() => setIsReplying(!isReplying)}>Responder</button>
          {comment.can_approve && (
            <button
              className="hover:text-green-600 disabled:opacity-50"
              onClick={handleModerate}
              disabled={moderateMutation.isPending}
            >
              {moderateMutation.isPending ? <i className="fa fa-spinner fa-spin" /> : <><i className="fa fa-check" /> Aprovar</>}
            </button>
          )}
          {comment.can_delete && (
            <button
              className="hover:text-red-500 disabled:opacity-50"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <i className="fa fa-spinner fa-spin" /> : "Eliminar"}
            </button>
          )}
        </div>
        {isReplying && (
          <div className="flex items-center gap-2 mt-1">
            <input
              type="text"
              className="flex-1 border border-gray-200 rounded px-2 py-1 text-sm"
              placeholder="Escreva uma resposta..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleReply() } }}
            />
            <button
              className="text-xs text-blue-500 disabled:opacity-50"
              onClick={handleReply}
              disabled={replyMutation.isPending}
            >
              {replyMutation.isPending ? <i className="fa fa-spinner fa-spin" /> : "Enviar"}
            </button>
          </div>
        )}
        {comment.childreens.length > 0 && (
          <div className="ml-4 mt-2 space-y-2">
            {comment.childreens.map((child) => (
              <CommentItem key={child.id} comment={child} postId={postId} onRefresh={onRefresh} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Comments
